import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { NIVEIS, DIMENSOES, nivelLabelFor, conceitoColor } from "@/lib/constants";
import { Save, CheckCircle2, Info, EyeOff, Loader2, ArrowLeft, Layers, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function AvaliarPad() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const back = new URLSearchParams(location.search).get("back") || "/avaliar";
  const [av, setAv] = useState(null);
  const [respostas, setRespostas] = useState({}); // criterio_id -> {nivel, nao_observado}
  const [obs, setObs] = useState("");
  const [fortes, setFortes] = useState("");
  const [melhorar, setMelhorar] = useState("");
  const [saving, setSaving] = useState(false);
  const [resumo, setResumo] = useState(null); // resultado retornado ao finalizar (aviso resumido)
  const [locked, setLocked] = useState(null); // mensagem quando outro instrutor detém a trava
  const [version, setVersion] = useState(0);
  const lockRef = useRef(null);

  useEffect(() => {
    api.get(`/avaliacoes/${id}`).then((r) => {
      const a = r.data;
      setAv(a);
      const map = {};
      (a.respostas || []).forEach((x) => { map[x.criterio_id] = { nivel: x.nivel, nao_observado: x.nao_observado }; });
      setRespostas(map);
      setObs(a.observacoes || "");
      setFortes(a.pontos_fortes || "");
      setMelhorar(a.pontos_melhorar || "");
      setVersion(a.version || 0);
    });
  }, [id]);

  // Trava colaborativa: adquire ao abrir; renova por heartbeat; libera ao sair. Se outro
  // instrutor já detém a trava, bloqueia a edição (somente visualização + Voltar).
  useEffect(() => {
    if (!av) return;
    const body = { turma_id: av.turma_id, oficina_id: av.oficina_id, aluno_id: av.aluno_id };
    lockRef.current = body;
    let hb = null; let cancelled = false;
    (async () => {
      try {
        await api.post("/collab/lock", body, { silent: true });
        if (cancelled) return;
        setLocked(null);
        hb = setInterval(async () => {
          try { await api.post("/collab/heartbeat", body, { silent: true }); }
          catch (e) { if (e.response?.status === 409) { setLocked(apiError(e.response?.data?.detail) || "Sua trava expirou."); if (hb) clearInterval(hb); } }
        }, 30000);
      } catch (e) {
        if (e.response?.status === 409) setLocked(apiError(e.response?.data?.detail));
      }
    })();
    return () => {
      cancelled = true;
      if (hb) clearInterval(hb);
      if (lockRef.current) api.post("/collab/unlock", lockRef.current, { silent: true }).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [av?.id]);

  const reloadAv = useCallback(async () => {
    try {
      const { data: a } = await api.get(`/avaliacoes/${id}`, { silent: true });
      setAv(a);
      const map = {};
      (a.respostas || []).forEach((x) => { map[x.criterio_id] = { nivel: x.nivel, nao_observado: x.nao_observado }; });
      setRespostas(map); setObs(a.observacoes || ""); setFortes(a.pontos_fortes || "");
      setMelhorar(a.pontos_melhorar || ""); setVersion(a.version || 0);
    } catch { /* noop */ }
  }, [id]);

  const setNivel = (cid, nivel) =>
    setRespostas((r) => ({ ...r, [cid]: { nivel, nao_observado: false } }));
  const setNaoObs = (cid) =>
    setRespostas((r) => ({ ...r, [cid]: { nivel: null, nao_observado: !r[cid]?.nao_observado } }));

  const buildPayload = useCallback(() => ({
    respostas: (av?.criterios || []).map((c) => ({
      criterio_id: c.id,
      nivel: respostas[c.id]?.nao_observado ? null : (respostas[c.id]?.nivel ?? null),
      nao_observado: !!respostas[c.id]?.nao_observado,
    })).filter((r) => r.nivel !== null || r.nao_observado),
    observacoes: obs, pontos_fortes: fortes, pontos_melhorar: melhorar,
  }), [av, respostas, obs, fortes, melhorar]);

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const r = await api.put(`/avaliacoes/${id}`, { ...buildPayload(), expected_version: version }, { silent: true });
      setVersion(r.data?.version ?? version);
      if (!silent) toast.success("Progresso salvo");
      return true;
    } catch (e) {
      const msg = apiError(e.response?.data?.detail);
      toast.error(msg);
      if (e.response?.status === 409) { if (/trava|avaliad/i.test(msg)) setLocked(msg); await reloadAv(); }
      return false;
    } finally { setSaving(false); }
  };

  const finalizar = async () => {
    setSaving(true);
    try {
      const putRes = await api.put(`/avaliacoes/${id}`, { ...buildPayload(), expected_version: version }, { silent: true });
      const nv = putRes.data?.version ?? (version + 1);
      setVersion(nv);
      const r = await api.post(`/avaliacoes/${id}/finalizar?expected_version=${nv}`, null, { silent: true });
      lockRef.current = null; // trava liberada no backend ao finalizar
      toast.success("Avaliação finalizada");
      setResumo(r.data?.resultado || {});
      setSaving(false);
    } catch (e) {
      const msg = apiError(e.response?.data?.detail);
      toast.error(msg);
      if (e.response?.status === 409) { if (/trava|avaliad/i.test(msg)) setLocked(msg); await reloadAv(); }
      setSaving(false);
    }
  };

  if (!av) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const criterios = av.criterios || [];
  const answered = criterios.filter((c) => respostas[c.id]?.nivel != null || respostas[c.id]?.nao_observado).length;
  const grupos = [];
  const gmap = {};
  criterios.forEach((c) => {
    const g = (c.grupo || "").trim() || "Critérios gerais";
    if (!gmap[g]) { gmap[g] = []; grupos.push(g); }
    gmap[g].push(c);
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      {/* Sidebar contexto */}
      <div className="lg:col-span-4 lg:sticky lg:top-6 space-y-4">
        <button data-testid="voltar-btn" onClick={() => navigate(back)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </button>
        <Card className="p-5 bg-card border-border">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">Aluno</div>
          <div className="font-heading text-2xl font-bold">{av.aluno_nome}</div>
          {av.aluno_nome_guerra && <div className="text-sm text-primary font-mono">"{av.aluno_nome_guerra}"</div>}
          {(() => {
            const isPMERJ = (av.aluno_instituicao || "").trim().toUpperCase() === "PMERJ";
            const label = isPMERJ ? "RG" : "Matrícula";
            const valor = isPMERJ ? av.aluno_rg : av.aluno_matricula;
            return valor ? <div className="text-xs text-muted-foreground font-mono" data-testid="aluno-doc-header">{label}: {valor}</div> : null;
          })()}
          <div className="mt-4 space-y-1.5 text-sm">
            <Row label="Curso" value={av.curso_nome} />
            <Row label="Turma" value={av.turma_nome} />
            <Row label="Oficina" value={av.oficina_nome} />
            {av.tipologia_cec_nome && <Row label="Tipologia CEC" value={av.tipologia_cec_nome} />}
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-xs mb-1.5">
              <span className="text-muted-foreground">Progresso</span>
              <span className="font-mono">{answered}/{criterios.length}</span>
            </div>
            <div className="h-2 rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${criterios.length ? (answered / criterios.length) * 100 : 0}%` }} />
            </div>
          </div>
        </Card>
        <div className="flex gap-2" data-sound="off">
          <Button variant="secondary" className="flex-1" data-testid="salvar-parcial-btn" onClick={() => save(false)} disabled={saving || !!locked}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Salvar
          </Button>
          <Button className="flex-1" data-testid="finalizar-btn" onClick={finalizar} disabled={saving || answered === 0 || !!locked}>
            <CheckCircle2 className="h-4 w-4 mr-2" />Finalizar
          </Button>
        </div>
      </div>

      {/* Critérios */}
      <div className="lg:col-span-8 space-y-6" data-sound="off">
        {grupos.map((g) => {
          const crits = gmap[g];
          return (
            <div key={g}>
              <div className="flex items-center gap-2 mb-3">
                <Layers className="h-5 w-5 text-primary" />
                <h2 className="font-heading text-xl font-bold uppercase tracking-tight">{g}</h2>
                <span className="text-xs text-muted-foreground font-mono">{crits.length}</span>
              </div>
              <div className="space-y-3">
                {crits.map((c) => {
                  const r = respostas[c.id] || {};
                  const D = DIMENSOES[c.dimensao];
                  return (
                    <Card key={c.id} data-testid={`criterio-card-${c.id}`} className="p-4 bg-card border-border">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="min-w-0">
                          <div className="font-medium">{c.nome}</div>
                          {c.descricao && <p className="text-xs text-muted-foreground mt-1" data-testid={`criterio-desc-${c.id}`}>{c.descricao}</p>}
                          <div className="flex items-center gap-2 mt-1">
                            {D && <Badge className={`${D.badge} text-[10px]`}>{D.label}</Badge>}
                            {c.critico && <Badge className="bg-red-500/15 text-red-300 border-red-500/40 text-[10px]">Crítico</Badge>}
                            <span className="text-xs text-muted-foreground">Peso {c.peso}</span>
                          </div>
                        </div>
                        {((c.definicoes && Object.keys(c.definicoes).length > 0) || (c.indicadores && c.indicadores.length > 0)) && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button data-testid={`info-${c.id}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary shrink-0"><Info className="h-4 w-4" /><span className="hidden sm:inline">O que observar</span></button>
                            </PopoverTrigger>
                            <PopoverContent className="bg-popover border-border w-80 max-h-[70vh] overflow-y-auto">
                              <div className="font-semibold text-sm mb-1">{c.nome}</div>
                              {c.referencia && <Badge className="bg-primary/15 text-primary border-primary/30 mb-2">Ref.: {c.referencia}</Badge>}
                              {c.descricao && <p className="text-xs text-muted-foreground mb-2">{c.descricao}</p>}
                              {c.indicadores && c.indicadores.length > 0 && (
                                <div className="mb-3">
                                  <div className="text-[11px] uppercase tracking-widest text-primary font-mono mb-1.5">O que observar</div>
                                  <ul className="space-y-1.5">
                                    {c.indicadores.map((ind, ii) => (
                                      <li key={ii} className="text-xs" data-testid={`indicador-${c.id}-${ii}`}>
                                        <span className="font-medium">• {ind.nome}</span>
                                        {ind.descricao && <span className="text-muted-foreground"> — {ind.descricao}</span>}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {c.definicoes && Object.keys(c.definicoes).length > 0 && (
                                <div className="space-y-1.5 border-t border-border pt-2">
                                  {NIVEIS.map((n) => c.definicoes[n.value] && (
                                    <div key={n.value} className="text-xs">
                                      <Badge className={`${n.badge} mr-2`}>{nivelLabelFor(n.value, av.escala_labels)}</Badge>
                                      {c.definicoes[n.value]}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                      <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
                        {NIVEIS.map((n) => {
                          const active = r.nivel === n.value;
                          return (
                            <button key={n.value} data-testid={`score-${c.id}-${n.value}`} onClick={() => setNivel(c.id, n.value)}
                              className={`min-h-[60px] sm:min-h-[64px] px-0.5 py-1 rounded-lg text-white font-semibold flex flex-col items-center justify-center gap-0.5 transition-all active:scale-95 ${n.cls} ${active ? "ring-[3px] ring-offset-2 ring-offset-card ring-[#D3A46F] scale-[1.05] shadow-lg shadow-black/30" : "hover:brightness-110 shadow-sm shadow-black/20"}`}>
                              <span className="text-lg sm:text-xl font-heading font-extrabold leading-none">{n.value}</span>
                              <span className="block text-[9px] sm:text-[10px] leading-tight text-center break-words w-full">{nivelLabelFor(n.value, av.escala_labels)}</span>
                            </button>
                          );
                        })}
                      </div>
                      <button data-testid={`naoobs-${c.id}`} onClick={() => setNaoObs(c.id)}
                        className={`mt-2 flex items-center gap-1.5 text-xs px-2 py-1 rounded transition-colors ${r.nao_observado ? "text-amber-300 bg-amber-500/15" : "text-muted-foreground hover:text-foreground"}`}>
                        <EyeOff className="h-3.5 w-3.5" /> Não observado
                      </button>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}

        <Card className="p-4 bg-card border-border space-y-3">
          <h3 className="font-heading text-lg font-bold uppercase tracking-tight">Observações</h3>
          <div>
            <label className="text-xs text-muted-foreground">Observações gerais</label>
            <Textarea data-testid="obs-geral" value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Ocorrências, orientações fornecidas..." />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-emerald-400">Ponto forte</label>
              <Textarea data-testid="obs-fortes" value={fortes} onChange={(e) => setFortes(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-orange-400">Ponto a melhorar / erro</label>
              <Textarea data-testid="obs-melhorar" value={melhorar} onChange={(e) => setMelhorar(e.target.value)} />
            </div>
          </div>
        </Card>

        {/* Ações duplicadas no final da tela (mesma função dos botões do topo) */}
        <div className="flex gap-2" data-testid="acoes-final">
          <Button variant="secondary" className="flex-1" data-testid="salvar-parcial-btn-final" onClick={() => save(false)} disabled={saving || !!locked}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Salvar
          </Button>
          <Button className="flex-1" data-testid="finalizar-btn-final" onClick={finalizar} disabled={saving || answered === 0 || !!locked}>
            <CheckCircle2 className="h-4 w-4 mr-2" />Finalizar
          </Button>
        </div>
      </div>

      {/* Aviso resumido ao finalizar (usa somente o resultado já calculado) */}
      <Dialog open={!!resumo} onOpenChange={(v) => { if (!v) { setResumo(null); navigate(back); } }}>
        <DialogContent className="max-w-md" data-testid="resumo-finalizacao">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">Avaliação finalizada</DialogTitle>
            <DialogDescription>Resumo do resultado — abra a ficha completa para ver os detalhes.</DialogDescription>
          </DialogHeader>
          {resumo && (resumo.sem_criterios ? (
            <div className="flex items-start gap-2 text-sm text-muted-foreground" data-testid="resumo-sem-criterios">
              <EyeOff className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" />
              <span>{resumo.mensagem || "Avaliação sem critérios observados suficientes para cálculo."}</span>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {resumo.aprovado != null && (
                  <Badge data-testid="resumo-situacao" className={`text-base px-3 py-1 ${resumo.aprovado ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-red-500/20 text-red-300 border-red-500/40"}`}>
                    {resumo.situacao || (resumo.aprovado ? "APTO" : "NÃO APTO")}
                  </Badge>
                )}
                <div className="text-sm">
                  <span className="text-muted-foreground">média </span>
                  <span className="font-heading text-2xl font-extrabold" data-testid="resumo-media">
                    {resumo.media_geral != null ? resumo.media_geral.toFixed(2).replace(".", ",") : "—"}
                  </span>
                  {resumo.nivel_geral?.label && (
                    <Badge className={`ml-2 ${conceitoColor(resumo.nivel_geral.label)}`}>{resumo.nivel_geral.label}</Badge>
                  )}
                </div>
              </div>
              {!!(resumo.criticos_alertas || []).length && (
                <div className="flex items-start gap-2 text-sm rounded-md bg-amber-500/10 border border-amber-500/30 p-2.5" data-testid="resumo-criticos">
                  <ShieldAlert className="h-5 w-5 shrink-0 text-amber-400 mt-0.5" />
                  <span>Atenção: {resumo.criticos_alertas.length} critério{resumo.criticos_alertas.length > 1 ? "s" : ""} crítico{resumo.criticos_alertas.length > 1 ? "s" : ""} abaixo do esperado</span>
                </div>
              )}
            </div>
          ))}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="secondary" data-testid="resumo-ver-ficha" onClick={() => { setResumo(null); navigate(`/avaliacoes/${id}?back=${encodeURIComponent(back)}`); }}>
              Ver ficha completa
            </Button>
            <Button data-testid="resumo-concluir" onClick={() => { setResumo(null); navigate(back); }}>
              Concluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bloqueio: aluno em avaliação por outro instrutor (somente visualização) */}
      <Dialog open={!!locked} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm" data-testid="aluno-bloqueado-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">Aluno em avaliação</DialogTitle>
            <DialogDescription>{locked}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button data-testid="bloqueado-voltar" onClick={() => navigate(back)}>Voltar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right truncate">{value}</span>
    </div>
  );
}
